import React, { useState, useEffect } from 'react';
import { useFormContext } from '../../context/FormContext';
import { Owner } from '../../types';
import AddressAutocomplete from '../AddressAutocomplete';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function monthFromBirthday(b?: string): string {
  return b?.split('/')?.[0] ?? '';
}

function dayFromBirthday(b?: string): string {
  return b?.split('/')?.[1] ?? '';
}

function buildBirthday(month: string, day: string): string {
  if (!month && !day) return '';
  return `${month}/${day}`;
}

export default function Section1SellerInfo() {
  const { state, saveSection, updateIntakeInfo } = useFormContext();
  const [propertyAddress, setPropertyAddress] = useState('');
  const [ownerCount, setOwnerCount] = useState(1);
  const [owners, setOwners] = useState<Owner[]>([
    { intake_id: '', owner_number: 1 },
    { intake_id: '', owner_number: 2 },
  ]);

  useEffect(() => {
    // Only initialize once — guard prevents re-running when formData updates after saves
    if (owners[0]?.intake_id) return;

    if (state.formData.owners && Array.isArray(state.formData.owners) && state.formData.owners.length > 0) {
      setOwners(state.formData.owners);
    } else if (state.intake) {
      // Pre-populate owner 1 from magic link data when no saved owner data exists
      setOwners(prev => prev.map((o, i) => i === 0 ? {
        ...o,
        full_name: o.full_name || state.intake!.seller_name || '',
        email: o.email || state.intake!.seller_email || '',
      } : o));
    }

    if (state.intake) {
      setPropertyAddress(state.intake.property_address);
      setOwnerCount(state.intake.owner_count);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.formData.owners, state.intake]);

  useEffect(() => {
    // Save owners when data changes
    if (!state.intake) return;

    const intakeOwners = owners.slice(0, ownerCount).map((owner, i) => ({
      ...owner,
      intake_id: state.intake!.id,
      owner_number: i + 1,
    }));

    const hasOwnerData = intakeOwners.some((o) => o.full_name || o.email || o.phone);
    if (!hasOwnerData) return;

    saveSection('owners', intakeOwners);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owners, ownerCount, state.intake]);

  // Debounce property address changes back to server
  useEffect(() => {
    if (!state.intake || !propertyAddress || propertyAddress === state.intake.property_address) return;
    const timer = setTimeout(() => {
      updateIntakeInfo({ property_address: propertyAddress });
    }, 1000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyAddress]);

  const updateOwner = (index: number, field: keyof Owner, value: string) => {
    const newOwners = [...owners];
    newOwners[index] = { ...newOwners[index], [field]: value };
    setOwners(newOwners);
  };

  const handleOwnerCountChange = (count: number) => {
    setOwnerCount(count);
    updateIntakeInfo({ owner_count: count });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Seller Information</h2>

        {/* Property Address */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Property Address *
          </label>
          <AddressAutocomplete
            value={propertyAddress}
            onChange={setPropertyAddress}
            placeholder="123 Main Street, City, State 12345"
          />
        </div>

        {/* Owner Count */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            How many owners are completing this form? *
          </label>
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="1"
                checked={ownerCount === 1}
                onChange={() => handleOwnerCountChange(1)}
                className="mr-2"
              />
              1 Owner
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="2"
                checked={ownerCount === 2}
                onChange={() => handleOwnerCountChange(2)}
                className="mr-2"
              />
              2 Owners
            </label>
          </div>
        </div>

        {/* Owner Information */}
        <div className="space-y-6">
          {owners.slice(0, ownerCount).map((owner, index) => (
            <div key={index} className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Owner {index + 1} Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={owner.full_name || ''}
                    onChange={(e) => updateOwner(index, 'full_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="John Doe"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={owner.email || ''}
                    onChange={(e) => updateOwner(index, 'email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="john@example.com"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    value={owner.phone || ''}
                    onChange={(e) => updateOwner(index, 'phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    placeholder="(555) 123-4567"
                  />
                </div>

                {/* Birthday — Month & Day only */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Birthday (Month &amp; Day)
                  </label>
                  <p className="text-xs text-gray-500 mb-2">We love celebrating our clients!</p>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={monthFromBirthday(owner.birthday)}
                      onChange={(e) => updateOwner(index, 'birthday', buildBirthday(e.target.value, dayFromBirthday(owner.birthday)))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    >
                      <option value="">Month</option>
                      {MONTHS.map((m, i) => (
                        <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>
                      ))}
                    </select>
                    <select
                      value={dayFromBirthday(owner.birthday)}
                      onChange={(e) => updateOwner(index, 'birthday', buildBirthday(monthFromBirthday(owner.birthday), e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                    >
                      <option value="">Day</option>
                      {Array.from({ length: 31 }, (_, i) => (
                        <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{i + 1}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Help Text */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Please complete as much as you can.</strong> If you don't know every answer right now, that's okay
            - you can skip items and return later. Your progress is automatically saved.
          </p>
        </div>
      </div>
    </div>
  );
}
